import React, { createContext, useCallback, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import { setGlobalLogoutHandler, resetApiState } from '../services/api';
import { setDefaultTimezone } from '../utils/dateUtils';

interface UserPrivilege {
  id: number;
  name: string;
  manage_users: boolean;
  all_patients: boolean;
  questionnaires: boolean;
  email_and_sms_templates: boolean;
  config: boolean;
  config_sync: boolean;
  file_types?: boolean;
  task_types?: boolean;
  wellness: boolean;
  prompt_development?: boolean;
  doc_assistant?: boolean;
  clinic_login?: boolean;
  hmo?: boolean;
  referral?: boolean;
  leads?: boolean;
  view_video_rooms?: boolean;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: 'admin' | 'therapist';
  tenant_id: number | null;
  timezone: string;
  is_2fa_enabled: boolean;
  privilege_id: number | null;
  user_privilege: UserPrivilege | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, totpToken?: string) => Promise<void>;
  authenticate: (email: string, password: string, totpToken?: string) => Promise<User>;
  authenticateGoogle: (credential: string, totpToken?: string) => Promise<User>;
  commitSession: (userData: User) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    resetApiState();
    setDefaultTimezone('Asia/Jerusalem');
    setUser(null);
    // Clearing the user is what swaps App over to the unauthenticated routes,
    // but on its own it leaves the URL pointing at wherever the person was -
    // and any screen still mounted keeps its stale data on display. Navigating
    // says plainly that they have been signed out, which is what a session
    // that expired under them needs to say. `replace` so Back does not return
    // to a page that can no longer load anything.
    navigate('/login', { replace: true });
  }, [navigate]);

  // Re-registered whenever logout changes so the api client never holds a
  // stale closure. Split from the auth check below, which must run once.
  useEffect(() => {
    setGlobalLogoutHandler(logout);
  }, [logout]);

  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const userData = await authService.getCurrentUser();
        setDefaultTimezone(userData.timezone);
        setUser(userData);
      }
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Store tokens + fetch the user, but DON'T activate the session yet — the
  // login page decides whether to prompt 2FA setup/verify before committing.
  const authenticate = async (email: string, password: string, totpToken?: string) => {
    const response = await authService.login(email, password, totpToken);
    localStorage.setItem('token', response.access_token);
    localStorage.setItem('refreshToken', response.refresh_token);
    return await authService.getCurrentUser();
  };

  const authenticateGoogle = async (credential: string, totpToken?: string) => {
    const response = await authService.loginWithGoogle(credential, totpToken);
    localStorage.setItem('token', response.access_token);
    localStorage.setItem('refreshToken', response.refresh_token);
    return await authService.getCurrentUser();
  };

  // Activate the session (sets the user, which swaps the app to the authed routes).
  const commitSession = (userData: User) => {
    setDefaultTimezone(userData.timezone);
    setUser(userData);
  };

  const login = async (email: string, password: string, totpToken?: string) => {
    const userData = await authenticate(email, password, totpToken);
    commitSession(userData);
  };


  const refreshUser = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setDefaultTimezone(userData.timezone);
      setUser(userData);
    } catch (error) {
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    authenticate,
    authenticateGoogle,
    commitSession,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};